/* eslint-disable */
import earcut from 'earcut';
import * as BABYLON from 'babylonjs';
// BabylonJS needs earcut to be available in the global window object.
if (window && !window.earcut) {
    window.earcut = earcut;
}

import Utils3D from '../utils/utils3D.js';
import Utils2D from '../../utils/utils2D.js';
import NonLinearRescaler from '../NonLinearRescaler.js';
import CoreUtils from '../../utils/coreUtils.js';
import Spline, { RESAMPLE_LAST_POINT_OPTION } from './spline.js';
import MeshInfo from './meshInfo.js';

export const EPSILON = 1e-4;
const SHARED_VERTICES_EPSILON = 1e-6;
export const UV_SCALE_MULT = 1.0/(1.0);

export default class GeoUtils
{
/*****************************************************************************/
static getDistanceToSegment2D(vecTestPoint, vecStart, vecEnd)
{
    let vecDir = vecEnd.subtract(vecStart);
    let fLength = vecDir.length();
    vecDir.normalize();

    let vecToPoint = vecTestPoint.subtract(vecStart);
    let fDistOnSegment = BABYLON.Vector2.Dot(vecToPoint, vecDir);

    // Get the normal distance
    let vecNormal = new BABYLON.Vector2(vecDir.y, -vecDir.x);
    let fPerpDist = Math.abs(BABYLON.Vector2.Dot(vecNormal, vecToPoint));

	let vecClosestPoint;
    let finalDist = fPerpDist;
	let isOnSegment = true;
    if(fDistOnSegment < 0)
	{
		finalDist = vecStart.subtract(vecTestPoint).length();
        fDistOnSegment = 0;
		vecClosestPoint = vecStart;
		isOnSegment = false;
	}
    else if(fDistOnSegment >= fLength)
	{
		finalDist = vecEnd.subtract(vecTestPoint).length();
        fDistOnSegment = fLength;
		vecClosestPoint = vecEnd;
		isOnSegment = false;
	}

	if(!vecClosestPoint)
		vecClosestPoint = vecStart.add(vecDir.scale(fDistOnSegment));

    return { normalDist: fPerpDist, distOnSegment: fDistOnSegment, smallestDist: finalDist, closestPoint: vecClosestPoint, isOnSegment: isOnSegment };
}
/*****************************************************************************/
static getDistanceToSegment3D(vecPoint, vecStart, vecEnd)
{
    let vecDir = vecEnd.subtract(vecStart);
    let fLength = vecDir.length();
    vecDir.normalize();

	let vecDiff1 = new BABYLON.Vector3(0,0,0);
	vecPoint.subtractToRef(vecStart, vecDiff1);

	let isOnSegment = true;
	let vecClosestPoint;
    let fSideDist = Number.MAX_VALUE;
	let finalDist;
	let fDist1 = BABYLON.Vector3.Dot(vecDiff1, vecDir);
    if(fDist1 < 0)
    {
        fSideDist = Math.abs(fDist1);
        fDist1 = 0;
		vecClosestPoint = vecStart;
		finalDist = vecDiff1.length();
		isOnSegment = false;
    }
    else if(fDist1 >= fLength)
	{
        fSideDist = Math.abs(fDist1 - fLength);
        fDist1 = fLength;
		vecClosestPoint = vecEnd;
		finalDist = vecPoint.subtract(vecEnd).length();
		isOnSegment = false;
	}        

	vecDir.scaleToRef(fDist1, vecDiff1);
	vecDir = vecDiff1;
	
	let vecPoint1 = new BABYLON.Vector3(0,0,0);
	vecStart.addToRef(vecDir, vecPoint1);
	
	let vecPoint2 = new BABYLON.Vector3(0,0,0);
	vecPoint1.subtractToRef(vecPoint, vecPoint2);

	let fPerpDist1 = vecPoint2.length(); 
	if(finalDist === undefined)
	{
		finalDist = fPerpDist1;
		vecClosestPoint = vecPoint1;
	}

	return { normalDist: fPerpDist1, distOnSegment: fDist1, smallestDist: finalDist, closestPoint: vecClosestPoint, isOnSegment: isOnSegment };
}
/*****************************************************************************/
static toJSCADMesh(pBabylonMesh)
{
	let positions = pBabylonMesh.getVerticesData(BABYLON.VertexBuffer.PositionKind);
    let normals = pBabylonMesh.getVerticesData(BABYLON.VertexBuffer.NormalKind);
	let indices = pBabylonMesh.getIndices();

	pBabylonMesh.computeWorldMatrix(true);
	let worldMatrix = pBabylonMesh.getWorldMatrix();

	let p0Index;
	let p1Index;
	let p2Index;

    const JSCAD_CONV_EPSILON = 1e-10;

//    console.log('------------------------------- toJSCADMesh -------------------------------');

    let jscadPolys = [];
	let index;
    let point1, point2, point3;
    let jscadVerts;
	for(index = 0; index < indices.length; index += 3)
	{
		p0Index = indices[index];
		p1Index = indices[index + 1];
		p2Index = indices[index + 2];
			
		point1 = new BABYLON.Vector3(positions[p0Index*3], positions[p0Index*3 + 1], positions[p0Index*3 + 2]);
		point2 = new BABYLON.Vector3(positions[p1Index*3], positions[p1Index*3 + 1], positions[p1Index*3 + 2]);
		point3 = new BABYLON.Vector3(positions[p2Index*3], positions[p2Index*3 + 1], positions[p2Index*3 + 2]);

        point1 = BABYLON.Vector3.TransformCoordinates(point1, worldMatrix);
        point2 = BABYLON.Vector3.TransformCoordinates(point2, worldMatrix);
        point3 = BABYLON.Vector3.TransformCoordinates(point3, worldMatrix);

        let faceNormal = BABYLON.Vector3.Cross(point2.subtract(point1), point3.subtract(point2));
        let fArea = faceNormal.length()*0.5;
        if(Math.abs(fArea) < 1e-6)
            continue;

        jscadVerts = [];

        jscadVerts[0] = new JSCad.Vertex(new JSCad.Vector3D(point1.x, point1.y, point1.z));
        jscadVerts[2] = new JSCad.Vertex(new JSCad.Vector3D(point2.x, point2.y, point2.z));
        jscadVerts[1] = new JSCad.Vertex(new JSCad.Vector3D(point3.x, point3.y, point3.z));

        const polygon = new JSCad.Polygon(jscadVerts);
        jscadPolys.push(polygon);
	}

    return JSCad.fromPolygons(jscadPolys);
}
/*****************************************************************************/
static fromJSCADMesh(pJSCadMesh, strName, scene)
{
    let positions = [];
    let indices = [];

    for(let polyIndex = 0; polyIndex < pJSCadMesh.polygons.length; polyIndex++)
	{
        const currOffset = positions.length/3;
        const pPoly = pJSCadMesh.polygons[polyIndex];

        let newPositions = [];
     
        for(let vIndex = 0; vIndex < pPoly.vertices.length; vIndex++)
        {
            newPositions.push(pPoly.vertices[vIndex].pos.x);
            newPositions.push(pPoly.vertices[vIndex].pos.y);
            newPositions.push(pPoly.vertices[vIndex].pos.z);
        }

        positions = positions.concat(newPositions);

        if(pPoly.vertices.length > 4)
		{
            // We need to find the polygon plane and project onto that:
            let currIdx = 0;
            let p1 = new BABYLON.Vector3(newPositions[currIdx*3], newPositions[currIdx*3 + 1], newPositions[currIdx*3 + 2]);
            currIdx = Math.floor(pPoly.vertices.length/3);
            let p2 = new BABYLON.Vector3(newPositions[currIdx*3], newPositions[currIdx*3 + 1], newPositions[currIdx*3 + 2]);
            currIdx = Math.floor(pPoly.vertices.length/3*2);
            let p3 = new BABYLON.Vector3(newPositions[currIdx*3], newPositions[currIdx*3 + 1], newPositions[currIdx*3 + 2]);
            let vec1 = p2.subtract(p1);
            let vec2 = p3.subtract(p2);
            let vecNorm = BABYLON.Vector3.Cross(vec1, vec2);
            let pVerts2D = [];
            let fSignedArea;

            // Just project onto the largest component
            if(Math.abs(vecNorm.x) >= Math.abs(vecNorm.y) && Math.abs(vecNorm.x) >= Math.abs(vecNorm.z))
			{
                for(let vIndex = 0; vIndex < pPoly.vertices.length; vIndex++)
                {
                    pVerts2D.push(pPoly.vertices[vIndex].pos.y);
                    pVerts2D.push(pPoly.vertices[vIndex].pos.z);
                }

                fSignedArea = GeoUtils.calculateSignedPolygonAreaXY([{x: p1.y, y: p1.z}, {x: p2.y, y: p2.z}, {x: p3.y, y: p3.z}]);
			}
            else if(Math.abs(vecNorm.y) >= Math.abs(vecNorm.x) && Math.abs(vecNorm.y) >= Math.abs(vecNorm.z))
			{
                for(let vIndex = 0; vIndex < pPoly.vertices.length; vIndex++)
                {
                    pVerts2D.push(pPoly.vertices[vIndex].pos.x);
                    pVerts2D.push(pPoly.vertices[vIndex].pos.z);
                }              

                fSignedArea = GeoUtils.calculateSignedPolygonAreaXY([{x: p1.x, y: p1.z}, {x: p2.x, y: p2.z}, {x: p3.x, y: p3.z}]);
			}
            else
			{
                for(let vIndex = 0; vIndex < pPoly.vertices.length; vIndex++)
                {
                    pVerts2D.push(pPoly.vertices[vIndex].pos.x);
                    pVerts2D.push(pPoly.vertices[vIndex].pos.y);
                }

                fSignedArea = GeoUtils.calculateSignedPolygonAreaXY([{x: p1.x, y: p1.y}, {x: p2.x, y: p2.y}, {x: p3.x, y: p3.y}]);
			}

            let tripletsOfIndices = earcut(pVerts2D, null, 2);
            if(fSignedArea < 0)
			{
                for(let tripletIdx = 0; tripletIdx < tripletsOfIndices.length; tripletIdx += 3)
			    {
                    indices.push(currOffset + tripletsOfIndices[tripletIdx + 0]);
                    indices.push(currOffset + tripletsOfIndices[tripletIdx + 1]);
                    indices.push(currOffset + tripletsOfIndices[tripletIdx + 2]);
			    }
			}
            else
			{
                for(let tripletIdx = 0; tripletIdx < tripletsOfIndices.length; tripletIdx += 3)
			    {
                    indices.push(currOffset + tripletsOfIndices[tripletIdx + 0]);
                    indices.push(currOffset + tripletsOfIndices[tripletIdx + 2]);
                    indices.push(currOffset + tripletsOfIndices[tripletIdx + 1]);
			    }
			}
		}
        else
        {
            indices.push(currOffset + 0);
            indices.push(currOffset + 2);
            indices.push(currOffset + 1);

            if(pPoly.vertices.length >= 4)
            {
                indices.push(currOffset + 2);
                indices.push(currOffset + 0);
                indices.push(currOffset + 3);
            }
        }
	}

    let customMesh = new BABYLON.Mesh(strName, scene);
    
    let vertexData = new BABYLON.VertexData();
    vertexData.positions = positions;
    vertexData.indices = indices;   
    vertexData.applyToMesh(customMesh);

    return customMesh;
}
/*****************************************************************************/
static calculateSignedPolygonAreaXY(points) 
{
    let area = 0.0;
    let p0, p1;
    for(let i = 0; i < points.length; i++)
    {
    	p0 = points[i];
    	p1 = points[(i + 1) % points.length];
    	area += (p0.x*p1.y) - (p1.x*p0.y)
    }
    return area*0.5;
}
/*****************************************************************************/
static calculateSignedPolygonAreaXZ(points) 
{
    let area = 0.0;
    let p0, p1;
    for(let i = 0; i < points.length; i++)
    {
    	p0 = points[i];
    	p1 = points[(i + 1) % points.length];
    	area += (p0.x*p1.z) - (p1.x*p0.z)
    }
    return area*0.5;
}
/*****************************************************************************/
static doRectanglesIntersectFloats(vecMinAx, vecMinAy, vecMaxAx, vecMaxAy, vecMinBx, vecMinBy, vecMaxBx, vecMaxBy)
{
    let aLeftOfB = vecMaxAx < vecMinBx;
    let aRightOfB = vecMinAx > vecMaxBx;
    let aAboveB = vecMinAy > vecMaxBy;
    let aBelowB = vecMaxAy < vecMinBy;

    return !(aLeftOfB || aRightOfB || aAboveB || aBelowB);
}
/*****************************************************************************/
static isPointAt(vecPoint, fX, fY, fZ)
{
    const EPSY = 0.00001;
    return Math.abs(vecPoint.x - fX) < EPSY && Math.abs(vecPoint.y - fY) < EPSY && Math.abs(vecPoint.z - fZ) < EPSY;
}
/*****************************************************************************/
static getFaceNormal(pMesh, iFaceId)
{
    let positions = pMesh.getVerticesData(BABYLON.VertexBuffer.PositionKind);
    let indices = pMesh.getIndices();
    let vertex1 = BABYLON.Vector3.FromArray(positions, indices[iFaceId*3]*3);
    let vertex2 = BABYLON.Vector3.FromArray(positions, indices[iFaceId*3 + 1]*3);
    let vertex3 = BABYLON.Vector3.FromArray(positions, indices[iFaceId*3 + 2]*3);

  	pMesh.computeWorldMatrix(true);
	let worldMatrix = pMesh.getWorldMatrix();

    vertex1 = BABYLON.Vector3.TransformCoordinates(vertex1, worldMatrix);
    vertex2 = BABYLON.Vector3.TransformCoordinates(vertex2, worldMatrix);
    vertex3 = BABYLON.Vector3.TransformCoordinates(vertex3, worldMatrix);

    let p1p2 = vertex1.subtract(vertex2);
    let p3p2 = vertex3.subtract(vertex2);
    return BABYLON.Vector3.Normalize(BABYLON.Vector3.Cross(p1p2, p3p2));
}
/*****************************************************************************/
static getRectCenter(rectIn)
{
	return new BABYLON.Vector2(rectIn.x + rectIn.w/2.0, rectIn.y + rectIn.h/2);
}
/*****************************************************************************/
static applyPathUVsToMeshes(pGeoList, spline, fXScale = 1.0, fXOffset = 0.0, bRotate90 = false, thickPolylineInfo = null)
{
    let pActualMeshes = Utils3D.getMeshesChildren(pGeoList);
	
    let vecDistances = [];
    let vecLinearPoints = spline.getLinearPoints3D();
	let fTotalDistance = Spline.computeDistancesOnPiecewiseLinear(vecLinearPoints, vecDistances, spline.getIsClosed());
    if(fTotalDistance <= 0)
        return;

    let perPointNormals = spline.computePerPointNormals(vecLinearPoints, new BABYLON.Vector3(0, 1, 0), true);
    let perPointDirections = Spline.computeSharpPerPointDirections(vecLinearPoints, spline.getIsClosed());
    let vecDiff = BABYLON.Vector3.Zero();
	let vecLocalPointPosition = BABYLON.Vector3.Zero();
	let vecGlobalPointPosition = BABYLON.Vector3.Zero();
	for(let pSrcMesh of pActualMeshes)
	{
        if(!pSrcMesh)
            continue;

		let uvs = pSrcMesh.getVerticesData(BABYLON.VertexBuffer.UVKind);
		if(!uvs)
			uvs = [];
		let positions = pSrcMesh.getVerticesData(BABYLON.VertexBuffer.PositionKind);
		let indices = pSrcMesh.getIndices();

		pSrcMesh.computeWorldMatrix(true);
		let currWorldMatrix = pSrcMesh.getWorldMatrix();

        let finalAlongCoord;
        let finalPerpCoord;
        let distIndex;
		for(let index = 0; index < indices.length; index += 3)
		{
			for(let subIndex = 0; subIndex < 3; subIndex++)
			{
				let pointIndex = indices[index + subIndex];
				vecLocalPointPosition.copyFromFloats(positions[pointIndex*3], positions[pointIndex*3 + 1], positions[pointIndex*3 + 2]);
				BABYLON.Vector3.TransformCoordinatesToRef(vecLocalPointPosition, currWorldMatrix, vecGlobalPointPosition);

                if(thickPolylineInfo)
				{
                    distIndex = thickPolylineInfo.points.findIndex((point) => { return BABYLON.Vector3.Distance(point, vecGlobalPointPosition) < 1e-4; });
                    finalAlongCoord = thickPolylineInfo.distances[distIndex];
                    finalPerpCoord = distIndex < thickPolylineInfo.points.length/2 ? 0 : 1;
				}
                else
				{
                    let closestPointInfo = spline.getPointClosestTo(vecGlobalPointPosition.x, vecGlobalPointPosition.y, vecGlobalPointPosition.z);

                    vecGlobalPointPosition.subtractToRef(closestPointInfo.point, vecDiff);
                    let dotProd = BABYLON.Vector3.Dot(perPointNormals[closestPointInfo.index], vecDiff);

                    let fromStartPoint = closestPointInfo.point.subtract(vecLinearPoints[closestPointInfo.index]);
                    let distAlongSegment = BABYLON.Vector3.Dot(fromStartPoint, perPointDirections[closestPointInfo.index]);
                    finalAlongCoord = vecDistances[closestPointInfo.index] + distAlongSegment;
                    finalPerpCoord = dotProd > 0.0 ? 1.0 : 0.0;
				}

                finalAlongCoord = finalAlongCoord*fXScale + fXOffset;
                if(bRotate90)
				{
				    uvs[pointIndex*2 + 0] = finalAlongCoord;
				    uvs[pointIndex*2 + 1] = finalPerpCoord;
				}
                else
				{
				    uvs[pointIndex*2 + 0] = finalPerpCoord;
				    uvs[pointIndex*2 + 1] = finalAlongCoord;
				}
			}
        }

        pSrcMesh.setVerticesData(BABYLON.VertexBuffer.UVKind, uvs, false, false);
    }
}
/*****************************************************************************/
static applyBoxUVsToMeshes(pGeoList, uOffset = 0, vOffset = 0, uvRotationDeg = 0, optOverrideBBox = null, uvOffsetsInWorldSpace = true)
{
	let pActualMeshes = Utils3D.getMeshesChildren(pGeoList);
	let bbox = optOverrideBBox ? optOverrideBBox : Utils3D.getObjectsBBox(pActualMeshes);
    const vecGlobalCenter = bbox.minimumWorld.add(bbox.maximumWorld).scale(0.5);

    const vecAxisZ = new BABYLON.Vector3(0, 0, 1);
    const vecAxisY = new BABYLON.Vector3(0, 1, 0);
    const vecAxisX = new BABYLON.Vector3(1, 0, 0);

    let vecLocalStartPoint = BABYLON.Vector3.Zero();
	for(let pSrcMesh of pActualMeshes)
	{
        if(!pSrcMesh || pSrcMesh.getTotalIndices() === 0)
            continue;

		let uvs = pSrcMesh.getVerticesData(BABYLON.VertexBuffer.UVKind);
		if(!uvs)
			uvs = [];
		let positions = pSrcMesh.getVerticesData(BABYLON.VertexBuffer.PositionKind);
		let indices = pSrcMesh.getIndices();

		// Transform this into the local matrix.
		let vecOldScaling = pSrcMesh.scaling.clone();
		pSrcMesh.scaling = new BABYLON.Vector3(1, 1, 1);

		pSrcMesh.computeWorldMatrix(true);
		let currWorldMatrix = pSrcMesh.getWorldMatrix();
		let invertedWorldMat = new BABYLON.Matrix();
		currWorldMatrix.invertToRef(invertedWorldMat);
		BABYLON.Vector3.TransformCoordinatesToRef(vecGlobalCenter, invertedWorldMat, vecLocalStartPoint);

		pSrcMesh.scaling = vecOldScaling;
		pSrcMesh.computeWorldMatrix(true);

        let faceSide1 = BABYLON.Vector3.Zero();
        let faceSide2 = BABYLON.Vector3.Zero();
        let faceNormal = BABYLON.Vector3.Zero();
        let vecDiff = BABYLON.Vector3.Zero();
        let pRotatedUVs = BABYLON.Vector2.Zero();
        let vecLocalPointPositions = [BABYLON.Vector3.Zero(), BABYLON.Vector3.Zero(), BABYLON.Vector3.Zero()];

		let pUVs = {
			x: null,
			y: null,
		};

		let processedUVs = new Set();
        let pointIndex;
        let subIndex;
		for(let index = 0; index < indices.length; index += 3)
		{
			for(subIndex = 0; subIndex < 3; subIndex++)
			{
				pointIndex = indices[index + subIndex];
				vecLocalPointPositions[subIndex].copyFromFloats(positions[pointIndex*3], positions[pointIndex*3 + 1], positions[pointIndex*3 + 2]);
			}

            vecLocalPointPositions[1].subtractToRef(vecLocalPointPositions[0], faceSide1);
            vecLocalPointPositions[2].subtractToRef(vecLocalPointPositions[1], faceSide2);
			BABYLON.Vector3.CrossToRef(faceSide1, faceSide2, faceNormal);
			faceNormal.normalize();

			let xyDiff = Math.abs(BABYLON.Vector3.Dot(vecAxisZ, faceNormal));
			let xzDiff = Math.abs(BABYLON.Vector3.Dot(vecAxisY, faceNormal));
			let zyDiff = Math.abs(BABYLON.Vector3.Dot(vecAxisX, faceNormal));
			for(subIndex = 0; subIndex < 3; subIndex++)
			{	
				pointIndex = indices[index + subIndex];

				if(processedUVs.has(pointIndex))
					continue;

				processedUVs.add(pointIndex);

				vecLocalPointPositions[subIndex].subtractToRef(vecLocalStartPoint, vecDiff);
				vecDiff.multiplyInPlace(pSrcMesh.scaling);

			    if(xyDiff >= xzDiff && xyDiff >= zyDiff)
			    {
    				pUVs.x = vecDiff.x;
	    			pUVs.y = vecDiff.y;

			    }
			    else if(xzDiff >= xyDiff && xzDiff >= zyDiff)
			    {
    				pUVs.x = vecDiff.x;
	    			pUVs.y = vecDiff.z;
			    }
			    else
			    {
    				pUVs.x = vecDiff.z;
	    			pUVs.y = vecDiff.y;
			    }
                			
				if(uvRotationDeg !== 0)
				{
					if(!uvOffsetsInWorldSpace)
					{
    					pUVs.x = pUVs.x*UV_SCALE_MULT - uOffset;
	    				pUVs.y = pUVs.y*UV_SCALE_MULT - vOffset;
					}
					Utils2D.rotatePointToRef(pUVs, uvRotationDeg, pRotatedUVs);
					pUVs.x = pRotatedUVs.x;
					pUVs.y = pRotatedUVs.y;
				}

    			uvs[pointIndex*2 + 0] = pUVs.x;
	    		uvs[pointIndex*2 + 1] = pUVs.y;
				if(uvOffsetsInWorldSpace)
				{
    				uvs[pointIndex*2 + 0] = uvs[pointIndex*2 + 0]*UV_SCALE_MULT - uOffset;
	    			uvs[pointIndex*2 + 1] = uvs[pointIndex*2 + 1]*UV_SCALE_MULT - vOffset;
				}
			}
		}

		pSrcMesh.setVerticesData(BABYLON.VertexBuffer.UVKind, uvs, false, false);
	}
}
/*****************************************************************************/
static removeInvalidTriangles(pSrcMesh)
{
	pSrcMesh.computeWorldMatrix();

	let positions = pSrcMesh.getVerticesData(BABYLON.VertexBuffer.PositionKind);
	let indices = pSrcMesh.getIndices();

	let faceSide1 = BABYLON.Vector3.Zero();
	let faceSide2 = BABYLON.Vector3.Zero();
	let faceNormal = BABYLON.Vector3.Zero();
	let faceArea;
	let vecLocalPointPositions = [BABYLON.Vector3.Zero(), BABYLON.Vector3.Zero(), BABYLON.Vector3.Zero()];

	let newIndices = [];

	for(let index = 0; index < indices.length; index += 3)
	{
		for(let subIndex = 0; subIndex < 3; subIndex++)
		{
			let pointIndex = indices[index + subIndex];
			vecLocalPointPositions[subIndex].copyFromFloats(positions[pointIndex*3], positions[pointIndex*3 + 1], positions[pointIndex*3 + 2]);
		}

		vecLocalPointPositions[1].subtractToRef(vecLocalPointPositions[0], faceSide1);
		vecLocalPointPositions[2].subtractToRef(vecLocalPointPositions[1], faceSide2);
		BABYLON.Vector3.CrossToRef(faceSide2, faceSide1, faceNormal);
		faceArea = faceNormal.length()*0.5;

		if(faceArea < 1e-8)
			continue;

		for(let subIndex = 0; subIndex < 3; subIndex++)
			newIndices.push(indices[index + subIndex]);
	}

	pSrcMesh.updateIndices(newIndices);
}
/*****************************************************************************/
static recomputeNormals(pMesh, fCreaseAngle, bUseVoxelGrid = undefined)
{
    pMesh.convertToFlatShadedMesh();

    if(fCreaseAngle < 1.0)
        return;

    const fCreaseAngleCosine = Math.cos(fCreaseAngle/180.0*Math.PI);

	if(bUseVoxelGrid === undefined || bUseVoxelGrid === null)
	{
		// Automatically decide based on vertex count:
		const numVertices = pMesh.getTotalVertices();
		bUseVoxelGrid = numVertices > 1000;
	}

	let meshInfo = new MeshInfo(pMesh);
	// We don't necessarily want to use the voxel grid for simple meshes
	// since it may take longer to compute and gather on each generation
	// rather than a straightforward iteration.
	meshInfo.computeAdjacentFaces(1e-12, 0.99, true, bUseVoxelGrid);

	let indices = pMesh.getIndices();
    let normals = pMesh.getVerticesData(BABYLON.VertexBuffer.NormalKind);
    let newNormals = normals.slice();
	let allFaces = meshInfo.getFaceInfos();

	const indexMult = indices.length*10;

	const ONE_EPSILON = 1.0 - 1e-7;

	let iCount = 0;
	let vecSum = BABYLON.Vector3.Zero();
	let vertInidices;
	let contributingPolyIndices = new Set();
	let processedVerts = new Set();
	for(let faceInfo of allFaces)
	{
		for(let iEdge = 0; iEdge < 3; iEdge++)
		{
			const currUniqueKey = faceInfo.index + indexMult*iEdge;
			if(processedVerts.has(currUniqueKey))
				continue;

			iCount = 0;
			vecSum.set(0, 0, 0);
			vertInidices = [];
			let testNormals = [];

			contributingPolyIndices.clear();

			let facesWithThisVertex = meshInfo.getFacesWithVertex(faceInfo.globalVerts[iEdge], true, 1e-10);
			for(let currFace of facesWithThisVertex)
			{
				let vertIndex = MeshInfo.getIndexForPoint(currFace, faceInfo.globalVerts[iEdge]);
				let incomingIndex = vertIndex - 1;
				if(incomingIndex < 0)
					incomingIndex += 3;

				const vertexKey = currFace.index + indexMult*vertIndex;
				processedVerts.add(vertexKey);

				// Now, see if this has an edge that is below threshold
				const adjacentNormal1 = currFace.adjacentFaces[vertIndex][0]?.info?.normal;
				const adjacentNormal2 = currFace.adjacentFaces[incomingIndex][0]?.info?.normal;

				const fDotProd1 = adjacentNormal1 ? BABYLON.Vector3.Dot(currFace.normal, adjacentNormal1) : -Number.MAX_VALUE;
				const fDotProd2 = adjacentNormal2 ? BABYLON.Vector3.Dot(currFace.normal, adjacentNormal2) : -Number.MAX_VALUE;

				if((fDotProd1 < fCreaseAngleCosine || fDotProd1 >= ONE_EPSILON)
					&& (fDotProd2 < fCreaseAngleCosine || fDotProd2 >= ONE_EPSILON))
						continue;

				let bAlreadyHaveNormal = false;
				for(let normal of testNormals)
				{
					if(BABYLON.Vector3.Dot(currFace.normal, normal) >= ONE_EPSILON)
					{
						bAlreadyHaveNormal = true;
						break;
					}
				}
				if(bAlreadyHaveNormal)
					continue;

				// This vertex has an edge that should be smoothed. Add this face's normal here.
				vecSum.addInPlace(currFace.normal);
				iCount++;

				testNormals.push(currFace.normal);
				let normalIndex = indices[currFace.index + vertIndex];
				vertInidices.push(normalIndex);
				contributingPolyIndices.add(currFace.index);
			}

			if(iCount <= 1)
				continue;

			// Find ones that are parallel to the ones that affect the normal, and set their normals, too
			for(let currFace of facesWithThisVertex)
			{
				if(contributingPolyIndices.has(currFace.index))
					continue;
			
				for(let normal of testNormals)
				{
					if(BABYLON.Vector3.Dot(currFace.normal, normal) >= ONE_EPSILON)
					{
						let vertIndex = MeshInfo.getIndexForPoint(currFace, faceInfo.globalVerts[iEdge]);
						let normalIndex = indices[currFace.index + vertIndex];
						vertInidices.push(normalIndex);
						contributingPolyIndices.add(currFace.index);
						break;
					}
				}
			}

			vecSum.scaleInPlace(1.0/iCount);
			vecSum.normalize();
			for(let normalIndex of vertInidices)
			{
				newNormals[normalIndex*3] = vecSum.x;
				newNormals[normalIndex*3 + 1] = vecSum.y;
				newNormals[normalIndex*3 + 2] = vecSum.z;
			}
		}
	}

	pMesh.setVerticesData(BABYLON.VertexBuffer.NormalKind, newNormals, true);
}
/*****************************************************************************/
static copyObjectsAlongSpline(pCopyObjects, pPathSpline, inputValues = {}, funcOnMeshCreated = null)
{
	if(!Array.isArray(pCopyObjects))
		pCopyObjects = [pCopyObjects];

	pPathSpline.ensureOrientation();

	let fCornerAngle = null;
	if(inputValues['preserve_corners'])
		fCornerAngle = inputValues['preserve_angle'];

    let fStart = 0;
    if(inputValues['start'] !== undefined)
        fStart = inputValues['start']/100.0;

    let fEnd = 1.0;
    if(inputValues['end'] !== undefined)
        fEnd = inputValues['end']/100.0;

	const bDoOrient = !!inputValues['orient_with_path'];
	if(inputValues['copy_type'] == 'resample')
	{
		if(inputValues['resample_type'] == 'byDistance') {
			const lastPoint = inputValues['extend_last_point'] ? RESAMPLE_LAST_POINT_OPTION.EXTEND : RESAMPLE_LAST_POINT_OPTION.DELETE;
			pPathSpline = pPathSpline.resampleByDistance(inputValues['spacing'], fStart, fEnd, fCornerAngle, lastPoint);
		}
		else
			pPathSpline = pPathSpline.resampleByNumber(inputValues['num_points'], fStart, fEnd, fCornerAngle);
	}
	else if(fStart > 0 || fEnd < 1)
	{
		// We're only copying to vertices, so we must leave only the ones within threshold
		let vecDistances = [];
		let vecLinearPoints = pPathSpline.getPointList();
		let fTotalDist = Spline.computeDistancesOnPiecewiseLinear(vecLinearPoints, vecDistances, pPathSpline.getIsClosed());
		let iStart;
		let iEnd;
		// Find start and end indices:
		for(let idx = 0; idx < vecLinearPoints.length; idx++)
		{
			let fInterp = vecDistances[idx]/fTotalDist;
			if(fInterp >= fStart && iStart === undefined)
			{
				iStart = idx;
				break;
			}
		}
		for(let idx = vecLinearPoints.length - 1; idx >= 0; idx--)
		{
			let fInterp = vecDistances[idx]/fTotalDist;
			if(fInterp <= fEnd && iEnd === undefined)
			{
				iEnd = idx;
				break;
			}
		}

		if(iStart !== undefined && iEnd !== undefined)
			pPathSpline = new Spline(vecLinearPoints.slice(iStart, iEnd + 1), false);
	}

	let pLinearPoints = pPathSpline.getLinearPoints(true, false, pPathSpline.getIs3D() ? null : 'xz');
	// We need to do this here instead of getting these out of the .resample* functions above because
	// those would not correctly produce directions we need while resampling a sharp-cornered curve -
	// these would be the smoothed directions, not directions between each actual point that we need here.
	let perPointDirs = Spline.computeSmoothedPerPointDirections(pLinearPoints);		

	let objectsBBox = Utils3D.getObjectsBBoxWithChildren(pCopyObjects);
	let vecCurrCenter = objectsBBox.maximumWorld.add(objectsBBox.minimumWorld).scale(0.5);

	let iTotalSum = 0;
	let patternArray = [];
    if(inputValues['copy_pattern'])
        patternArray = CoreUtils.replaceAll(inputValues['copy_pattern'], ' ', '').split(',');
	patternArray.forEach((value, index) =>
	{
		patternArray[index] = Math.abs(parseInt(value));
		iTotalSum += patternArray[index];
	});

	if(patternArray.length < 1 || iTotalSum < 1)
		patternArray = [1];

	const bIs3D = pLinearPoints.length > 0 && pLinearPoints[0].z !== undefined;
	let vecDir1 = bIs3D ? BABYLON.Vector3.Zero() : BABYLON.Vector2.Zero();
	let vecDir2 = bIs3D ? BABYLON.Vector3.Zero() : BABYLON.Vector2.Zero();

	let preserveCornersCosine = Math.cos(inputValues['corner_angle']/180.0*Math.PI);

	const bInvertPattern = inputValues['invert_pattern'];

	let iCounter = 0;
	let pFinalMesh;
	let pFinalMeshes = [];
	let vecTarget = BABYLON.Vector3.Zero();
	let vecNorm  = BABYLON.Vector3.Zero();
	let iCurrPatternCount = 0;
	let iPatternPos = 0;

	let instanceMeshMap = {};

	for(let pointIndex = 0; pointIndex < pLinearPoints.length; pointIndex++)
	{
		// First, see if we need to skip this angle:
		if(inputValues['corner_behavior'] != 'ignore')
		{
			let fDotProd = Spline.computePointAngleCos(pLinearPoints, pointIndex, pPathSpline.getIsClosed(), vecDir1, vecDir2);
			let bIsSharp = fDotProd - 1e-4 < preserveCornersCosine;
			if((bIsSharp && inputValues['corner_behavior'] == 'skipsharp')
				|| (!bIsSharp && inputValues['corner_behavior'] == 'onlysharp'))
			{
				if(inputValues['reset_pattern_after_corners'])
				{
					iCurrPatternCount = 0; 
					iPatternPos = 0;
				}
				continue;
			}
		}

		if(iCurrPatternCount >= patternArray[iPatternPos])
		{
			// Move on to the next pattern, skip zero ones
			iCurrPatternCount = 0;
			do
			{
				iPatternPos = (iPatternPos + 1) % patternArray.length;
			}
			while(patternArray[iPatternPos] < 1);
		}

		iCurrPatternCount++;

		if((iPatternPos % 2 === 1 && !bInvertPattern) || (iPatternPos % 2 === 0 && bInvertPattern))
			continue;

		let pPoint = pLinearPoints[pointIndex];

		let vecDir = perPointDirs[pointIndex % perPointDirs.length];
		// Can happen if we only have one point
		if(!vecDir)
			vecDir = new BABYLON.Vector3(1, 0, 0);
		vecNorm.x = -vecDir.z;
		vecNorm.z = vecDir.x;
		vecNorm.y = vecDir.y;

        let perpOffset = 0;
        if(inputValues['perp_offset'])
            perpOffset = inputValues['perp_offset'];

        let bUseInstances;
        let instanceId;
        const bAskPerCopy = typeof inputValues['use_instances'] === 'function';

		let vecShift = pPoint.subtract(vecCurrCenter).add(vecNorm.scale(perpOffset));
		for(let pSrcMesh of pCopyObjects)
		{
			if(!pSrcMesh)
				continue;

            if(bAskPerCopy)
			{
                instanceId = inputValues['use_instances']();
                bUseInstances = !!instanceId;
			}
            else
			{
                instanceId = pSrcMesh.uniqueId;
                bUseInstances = !!inputValues['use_instances'];
			}

			let bCreateInstance = !!(bUseInstances && instanceMeshMap[instanceId]);
			let trueSourceMesh = bCreateInstance ? instanceMeshMap[instanceId] : pSrcMesh;
			pFinalMesh = Utils3D.cloneMesh(trueSourceMesh, `${pSrcMesh.name}_${iCounter}`, true, bCreateInstance);
			if(bUseInstances && !instanceMeshMap[instanceId])
				instanceMeshMap[instanceId] = pFinalMesh;
			iCounter++;

            if(funcOnMeshCreated)
                funcOnMeshCreated(pFinalMesh, bCreateInstance);

			pFinalMesh.position = pFinalMesh.position.add(vecShift);
			if(bCreateInstance)
				pFinalMesh.position.subtractInPlace(instanceMeshMap[instanceId].position.subtract(pSrcMesh.position));

			if(bDoOrient)
			{
				pFinalMesh.position.addToRef(vecDir, vecTarget);
				pFinalMesh.lookAt(vecTarget);
				pFinalMesh.rotation = pFinalMesh.rotation.add(pSrcMesh.rotation);
			}

			pFinalMeshes.push(pFinalMesh);
		}
	}

	return pFinalMeshes;
}
/*****************************************************************************/
static getCoordSystemForBendAxis(strAxis)
{
	let vecDirAxis, vecSideAxis;
	let vecRotAxis;
	let strRotationAccessor;
	if(strAxis == 'yaxis')
	{
		vecRotAxis = new BABYLON.Vector3(0, 1, 0);
		vecDirAxis = new BABYLON.Vector3(0, 0, 1);
		vecSideAxis = new BABYLON.Vector3(1, 0, 0);
		strRotationAccessor = 'y';
	}
	else if(strAxis == 'xaxis')
	{
		vecRotAxis = new BABYLON.Vector3(1, 0, 0);
		vecDirAxis = new BABYLON.Vector3(0, 0, 1);
		vecSideAxis = new BABYLON.Vector3(0, 1, 0);

		strRotationAccessor = 'x';
	}
	else if(strAxis == 'zaxis')
	{
		vecRotAxis = new BABYLON.Vector3(0, 0, 1);
		vecDirAxis = new BABYLON.Vector3(0, 1, 0);
		vecSideAxis = new BABYLON.Vector3(1, 0, 0);

		strRotationAccessor = 'z';
	}

	return {
		rotAxis: vecRotAxis,
		dirAxis: vecDirAxis,
		sideAxis: vecSideAxis,
		rotAxisLetter: strRotationAccessor
	};
}

static bendSingleMesh(pMesh, fDegAmount, fOrientationDegs, strAxis, bboxMin = null, bboxMax = null, vecRelativeToPoint = null, bIsImportedMesh = false)
{
    const fRadAmount = fDegAmount/180.0*Math.PI;
	if(Math.abs(fRadAmount) < 1e-3)
		return;

    const fOrientationRads = fOrientationDegs/180.0*Math.PI;

	// For imported meshes we need to only orient them, we can't transform their vertices
	let positions;
	let vecPivotPosDiff;
	if(bIsImportedMesh)
	{
		let vecActualMeshPos = pMesh.getAbsolutePosition();
		vecPivotPosDiff = pMesh.position.subtract(vecActualMeshPos);
		positions = [vecActualMeshPos.x, vecActualMeshPos.y, vecActualMeshPos.z];
	}
	else
		positions = pMesh.getVerticesData(BABYLON.VertexBuffer.PositionKind);			

    // TODO: Should this be local, except for rotation?
    if(!bboxMin || !bboxMax)
	{
        pMesh.computeWorldMatrix(true);
        let pBBox = pMesh.getBoundingInfo().boundingBox;
        bboxMin = pBBox.minimumWorld;
        bboxMax = pBBox.maximumWorld;
	}

	let newPositions = [];
	let vecHalfDist = bboxMax.subtract(bboxMin).scale(0.5);

	let vecTemp = BABYLON.Vector3.Zero();
	let vecFinalPoint = BABYLON.Vector3.Zero();

	let vecDiff = BABYLON.Vector3.Zero();
	let vecDiff2 = BABYLON.Vector3.Zero();

	let pCoordSystem = GeoUtils.getCoordSystemForBendAxis(strAxis);

	let rotatedDirAxis = Utils3D.rotatePointAroundAxis(pCoordSystem.rotAxis, fOrientationRads, pCoordSystem.dirAxis);
	let fProjectedHalfDist = BABYLON.Vector3.Dot(vecHalfDist, rotatedDirAxis);
	let origCircleRad = fProjectedHalfDist/fRadAmount;

    if(!vecRelativeToPoint)
        vecRelativeToPoint = bboxMax.add(bboxMin).scale(0.5);

	// Build our total matrix that converts to local space, subtracts from ref point,
	// then rotates by the orientation parameter.
	pMesh.computeWorldMatrix(true);
	let worldMatrix = pMesh.getWorldMatrix();
	if(bIsImportedMesh)
		worldMatrix = BABYLON.Matrix.Identity();

	let matMakeRelative = BABYLON.Matrix.Translation(-vecRelativeToPoint.x, -vecRelativeToPoint.y, -vecRelativeToPoint.z);
	let matOrientationRotation = BABYLON.Matrix.RotationAxis(pCoordSystem.rotAxis, fOrientationRads);
	let finalTotalTransformMatrix = worldMatrix.multiply(matMakeRelative).multiply(matOrientationRotation);

	let invFinalTransformMatrix = BABYLON.Matrix.Identity();
	finalTotalTransformMatrix.invertToRef(invFinalTransformMatrix);

	let fCurrAngle;
	let fSin;
	let fCos;
	let circleRad;
	let fRadiusDist;
	let fProjectedDiff;
	let fMult;
	for(let index = 0; index < positions.length; index += 3)
	{		
		vecTemp.x = positions[index];
		vecTemp.y = positions[index + 1];
		vecTemp.z = positions[index + 2];

		BABYLON.Vector3.TransformCoordinatesToRef(vecTemp, finalTotalTransformMatrix, vecDiff);

		fProjectedDiff = BABYLON.Vector3.Dot(vecDiff, pCoordSystem.dirAxis);
		fMult = fProjectedDiff/fProjectedHalfDist;

		fCurrAngle = fRadAmount*fMult;
		fCos = Math.cos(fCurrAngle);
		fSin = Math.sin(fCurrAngle);

		fRadiusDist = BABYLON.Vector3.Dot(pCoordSystem.sideAxis, vecDiff);
		circleRad = origCircleRad + fRadiusDist;

		if(strAxis == 'yaxis')
		{
			vecDiff2.x = fCos*circleRad - origCircleRad;
			vecDiff2.y = vecDiff.y;
			vecDiff2.z = fSin*circleRad;			
		}
		else if(strAxis == 'xaxis')
		{
			vecDiff2.x = vecDiff.x;
			vecDiff2.y = fCos*circleRad - origCircleRad;
			vecDiff2.z = fSin*circleRad;
		}
		else if(strAxis == 'zaxis')
		{
			vecDiff2.x = fCos*circleRad - origCircleRad;
			vecDiff2.y = fSin*circleRad;
			vecDiff2.z = vecDiff.z;
		}

		BABYLON.Vector3.TransformCoordinatesToRef(vecDiff2, invFinalTransformMatrix, vecFinalPoint);

		if(bIsImportedMesh)
		{
			pMesh.rotation[pCoordSystem.rotAxisLetter] -= fCurrAngle;
			let vecActualMeshPos = pMesh.getAbsolutePosition();
			vecPivotPosDiff = pMesh.position.subtract(vecActualMeshPos);
			pMesh.position = vecFinalPoint.add(vecPivotPosDiff);
		}
		else
		{
			newPositions[index] = vecFinalPoint.x;
			newPositions[index + 1] = vecFinalPoint.y;
			newPositions[index + 2] = vecFinalPoint.z;
		}
	}

	if(!bIsImportedMesh)
		pMesh.setVerticesData(BABYLON.VertexBuffer.PositionKind, newPositions, true);
}
/*****************************************************************************/
static splitFace(pMesh, vecPosition)
{
    if(!pMesh)
        return false;

    pMesh.computeWorldMatrix();
	let worldMatrix = pMesh.getWorldMatrix();
	let invertedWorldMat = new BABYLON.Matrix();
	worldMatrix.invertToRef(invertedWorldMat);

    // Make sure we don't hit on a vertex exactly:
    //let worldVertexPositions = Utils3D.getMeshVertexGlobalPositions(pMesh);
    // Ignore edges where this vertex is already part of the triangle.
    // We can't just bail if we hit one of these because may need to fix degenerate
    // geometry where a vertex is properly part of one face, but only lies on the
    // edge of another face without it being properly split.
    let meshInfo = new MeshInfo(pMesh, 0);
    let facesWithVertex = meshInfo.getFacesWithVertex(vecPosition, false);

    let ignoredFaces = new Set();
    facesWithVertex.forEach((faceInfo) => { ignoredFaces.add(faceInfo.index); });

    // Find which face this belongs to:
    let iNumPointsToAdd = 1;
    let firstEdgeInfo = meshInfo.getFaceContainingPoint(vecPosition, ignoredFaces, -1e-6);
    if(!firstEdgeInfo)
        return false;

    // Now, we must make sure it's not exactly on an edge:
    let firstEdgeVertexIndex = -1;
    let sharedEdgeAdjacentInfo = null;
    let sharedEdgeVertexIndex = -1;

    let containingEdgeInfos = meshInfo.getEdgesContainingPoint(vecPosition, ignoredFaces, false, undefined, false);
    if(containingEdgeInfos.length > 0)
    {
        firstEdgeInfo = containingEdgeInfos[0].faceInfo;
        firstEdgeVertexIndex = containingEdgeInfos[0].vertexIndex;
        if(containingEdgeInfos.length > 1)
		{
            sharedEdgeAdjacentInfo = containingEdgeInfos[1].faceInfo;
            sharedEdgeVertexIndex = containingEdgeInfos[1].vertexIndex;
            iNumPointsToAdd = 2;
		}
    }

	let oldPositions = pMesh.getVerticesData(BABYLON.VertexBuffer.PositionKind);
	let oldNormals = pMesh.getVerticesData(BABYLON.VertexBuffer.NormalKind);
	let oldUvs = pMesh.getVerticesData(BABYLON.VertexBuffer.UVKind);
	let indices = pMesh.getIndices();

    let vecLocalSpaceVertex = BABYLON.Vector3.Zero();

	let positions = new Float32Array(oldPositions.length + 3*iNumPointsToAdd);
	positions.set(oldPositions);

	let normals = new Float32Array(oldNormals.length + 3*iNumPointsToAdd);
	normals.set(oldNormals);

    let uvs = null;
    if(oldUvs)
	{
	    uvs = new Float32Array(oldUvs.length + 2*iNumPointsToAdd);
	    uvs.set(oldUvs);
	}

	let faceIndex = firstEdgeInfo.index; // *3
    let face1Bary = Utils3D.computeBarycentricCoords(vecPosition, firstEdgeInfo.globalVerts);
// TEMP for testing
//vecPosition.y = 0;
	BABYLON.Vector3.TransformCoordinatesToRef(vecPosition, invertedWorldMat, vecLocalSpaceVertex);

	let oldVertexIndex1 = indices[faceIndex];
	let oldVertexIndex2 = indices[faceIndex + 1];
	let oldVertexIndex3 = indices[faceIndex + 2];

	let newPointArrayPos = oldPositions.length;
	positions[newPointArrayPos] = vecLocalSpaceVertex.x;
	positions[newPointArrayPos + 1] = vecLocalSpaceVertex.y;
	positions[newPointArrayPos + 2] = vecLocalSpaceVertex.z;

	let newNormalIndex = oldNormals.length;
	normals[newNormalIndex] = oldNormals[oldVertexIndex1*3]*face1Bary.u + oldNormals[oldVertexIndex2*3]*face1Bary.v + oldNormals[oldVertexIndex3*3]*face1Bary.w;;
	normals[newNormalIndex + 1] = oldNormals[oldVertexIndex1*3 + 1]*face1Bary.u + oldNormals[oldVertexIndex2*3 + 1]*face1Bary.v + oldNormals[oldVertexIndex3*3 + 1]*face1Bary.w;;
	normals[newNormalIndex + 2] = oldNormals[oldVertexIndex1*3 + 2]*face1Bary.u + oldNormals[oldVertexIndex2*3 + 2]*face1Bary.v + oldNormals[oldVertexIndex3*3 + 2]*face1Bary.w;;

	let newUVsIndex = oldUvs.length;
    if(uvs)
	{
	    uvs[newUVsIndex] = oldUvs[oldVertexIndex1*2]*face1Bary.u + oldUvs[oldVertexIndex2*2]*face1Bary.v + oldUvs[oldVertexIndex3*2]*face1Bary.w;
	    uvs[newUVsIndex + 1] = oldUvs[oldVertexIndex1*2 + 1]*face1Bary.u + oldUvs[oldVertexIndex2*2 + 1]*face1Bary.v + oldUvs[oldVertexIndex3*2 + 1]*face1Bary.w;
	}

	let iNewPointIndex = newPointArrayPos/3;

    //let triangleCheckIndices = [];

    if(firstEdgeVertexIndex >= 0)
	{
        let newPointArrayPos2;
        if(iNumPointsToAdd > 1)
		{
            let face2Bary = Utils3D.computeBarycentricCoords(vecPosition, sharedEdgeAdjacentInfo.globalVerts);
	        
	        let sharedOldVertexIndex1 = indices[sharedEdgeAdjacentInfo.index + (2 + 1) % 3];
	        let sharedOldVertexIndex2 = indices[sharedEdgeAdjacentInfo.index + (2 + 2) % 3];
	        let sharedOldVertexIndex3 = indices[sharedEdgeAdjacentInfo.index + (2 + 3) % 3];

	        newPointArrayPos2 = oldPositions.length + 3;
	        positions[newPointArrayPos2] = vecLocalSpaceVertex.x;
	        positions[newPointArrayPos2 + 1] = vecLocalSpaceVertex.y;
	        positions[newPointArrayPos2 + 2] = vecLocalSpaceVertex.z;
            
	        let newNormalIndex2 = oldNormals.length + 3;
	        normals[newNormalIndex2] = oldNormals[sharedOldVertexIndex1*3]*face2Bary.u + oldNormals[sharedOldVertexIndex2*3]*face2Bary.v + oldNormals[sharedOldVertexIndex3*3]*face2Bary.w;
	        normals[newNormalIndex2 + 1] = oldNormals[sharedOldVertexIndex1*3 + 1]*face2Bary.u + oldNormals[sharedOldVertexIndex2*3 + 1]*face2Bary.v + oldNormals[sharedOldVertexIndex3*3 + 1]*face2Bary.w;
	        normals[newNormalIndex2 + 2] = oldNormals[sharedOldVertexIndex1*3 + 2]*face2Bary.u + oldNormals[sharedOldVertexIndex2*3 + 2]*face2Bary.v + oldNormals[sharedOldVertexIndex3*3 + 2]*face2Bary.w;

	        let newUVsIndex2 = oldUvs.length + 2;

            if(uvs)
			{
   	            uvs[newUVsIndex2] = oldUvs[sharedOldVertexIndex1*2]*face2Bary.u + oldUvs[sharedOldVertexIndex2*2]*face2Bary.v + oldUvs[sharedOldVertexIndex3*2]*face2Bary.w;
	            uvs[newUVsIndex2 + 1] = oldUvs[sharedOldVertexIndex1*2 + 1]*face2Bary.u + oldUvs[sharedOldVertexIndex2*2 + 1]*face2Bary.v + oldUvs[sharedOldVertexIndex3*2 + 1]*face2Bary.w;
			}
        }

        // We have a vertex-on-an-edge situation
        let oldIndex = indices[firstEdgeInfo.index + (firstEdgeVertexIndex + 1) % 3];
        indices[firstEdgeInfo.index + (firstEdgeVertexIndex + 1) % 3] = iNewPointIndex;
        //triangleCheckIndices.push(firstEdgeInfo.index);

        //triangleCheckIndices.push(indices.length);
	    indices.push(iNewPointIndex);
	    indices.push(oldIndex);
        let iTempValue = indices[firstEdgeInfo.index + (firstEdgeVertexIndex + 2) % 3];
	    indices.push(iTempValue);

        if(iNumPointsToAdd > 1)
		{
	        let iNewPointIndex2 = newPointArrayPos2/3;

            oldIndex = indices[sharedEdgeAdjacentInfo.index + (sharedEdgeVertexIndex + 1) % 3];
            indices[sharedEdgeAdjacentInfo.index + (sharedEdgeVertexIndex + 1) % 3] = iNewPointIndex2;
            //triangleCheckIndices.push(sharedEdgeAdjacentInfo.index);

            //triangleCheckIndices.push(indices.length);
	        indices.push(iNewPointIndex2);
	        indices.push(oldIndex);
	        indices.push(indices[sharedEdgeAdjacentInfo.index + (sharedEdgeVertexIndex + 2) % 3]);
		}
	}
    else
	{
        // This modifies the existing face
	    indices[faceIndex] = oldVertexIndex1;
	    indices[faceIndex + 1] = oldVertexIndex2;
	    indices[faceIndex + 2] = iNewPointIndex;
        //triangleCheckIndices.push(faceIndex);
				
        // This adds two more faces
        //triangleCheckIndices.push(indices.length);
	    indices.push(iNewPointIndex);
	    indices.push(oldVertexIndex3);
	    indices.push(oldVertexIndex1);

        //triangleCheckIndices.push(indices.length);
	    indices.push(oldVertexIndex2);
	    indices.push(oldVertexIndex3);
	    indices.push(iNewPointIndex);
	}

    // TODO: Check orientations? From testing, seem unnecessary, though.
				
	let vertexData = new BABYLON.VertexData();
	vertexData.positions = positions;
	vertexData.indices = indices;
	vertexData.normals = normals;
    if(uvs)
	    vertexData.uvs = uvs;

	vertexData.applyToMesh(pMesh);

    return true;
}
/*****************************************************************************/
static splitOverlappingMeshEdges(pMesh, pOptMeshInfo = null)
{
    if(!pMesh)
        return;

    let meshInfo = pOptMeshInfo ? pOptMeshInfo : new MeshInfo(pMesh, 0);

    let excludedFacesSet = new Set();
    let pointsToSplitAt = [];
    for(let faceInfo of meshInfo.getFaceInfos())
	{
        excludedFacesSet.clear();
        excludedFacesSet.add(faceInfo.index);
        for(let subIdx = 0; subIdx < 3; subIdx++)
		{
            let containingEdgeInfos = meshInfo.getEdgesContainingPoint(faceInfo.globalVerts[subIdx], excludedFacesSet, false, -1e-2, false);
            if(containingEdgeInfos.length > 0)
            {
                // Avoid duplicates - this is probably fine N^2 for now, since we shouldn't have too many of these:
                let bHaveDuplicate = false;
                for(let currPoint of pointsToSplitAt)
				{
                    if(BABYLON.Vector3.Distance(currPoint, faceInfo.globalVerts[subIdx]) < SHARED_VERTICES_EPSILON)
					{
                        bHaveDuplicate = true;
                        break;
					}
				}

                if(!bHaveDuplicate)
                    pointsToSplitAt.push(faceInfo.globalVerts[subIdx]);
            }
		}
	}

    let iNumSplits = 0;
    let iNumFailed = 0;
    for(let vecPoint of pointsToSplitAt)
    {
        let bDidSplit = GeoUtils.splitFace(pMesh, vecPoint);
        if(bDidSplit)
            iNumSplits++;
        else
            iNumFailed++;
    }

    CoreUtils.log(`TVertex Fixes: Split ${iNumSplits}, failed ${iNumFailed}`);

    return { numSplits: iNumSplits, numFailed: iNumFailed };
}
/*****************************************************************************/
static projectPointsOntoPlane(points, vecPlaneNormal, vecOptCenter = null)
{
	if(points.length <= 0)
		return [];

	let vecCenter = vecOptCenter;
	if(!vecCenter)
		vecCenter = points[0];

	let vecUpAxisOut = BABYLON.Vector3.Zero();
	let vecSideAxisOut = BABYLON.Vector3.Zero();
	Utils3D.getCoordSystemFromNormal(vecPlaneNormal, vecUpAxisOut, vecSideAxisOut);

	let pRes = [];
	for(let vecPoint of points)
	{
		let vecToCenter = vecPoint.subtract(vecCenter);

		let fX = BABYLON.Vector3.Dot(vecToCenter, vecSideAxisOut);
		let fY = BABYLON.Vector3.Dot(vecToCenter, vecUpAxisOut);

		pRes.push(new BABYLON.Vector2(fX, fY));
	}

	return pRes;
}
/*****************************************************************************/
static debugVisualizeSubmeshes(pMesh, bjsScene, optColorGetter = null)
{
    if(!pMesh.material.subMaterials)
        return;

    pMesh.material.subMaterials = [];
	for(let submesh of pMesh.subMeshes)
	{
		let testMat = new BABYLON.StandardMaterial(`test_mat`, bjsScene);
        if(optColorGetter)
            testMat.diffuseColor = optColorGetter(submesh);
        else
		    testMat.diffuseColor = new BABYLON.Color3.Random();        
        testMat.emissiveColor = testMat.diffuseColor.scale(0.3);

        pMesh.material.subMaterials.push(testMat);
        submesh.materialIndex = pMesh.material.subMaterials.length - 1;
    }
}
/*****************************************************************************/
static scaleMesh(pSrcMesh, scaleX, scaleY, scaleZ, bInWorldSpace = false, nlrsScalingFlags = null)
{
	if(scaleX == 1.0 && scaleY == 1.0 && scaleZ == 1.0)
        return;

	let naiveScales = [1, 1, 1];
	let nlrsScales = [1, 1, 1];
    if(nlrsScalingFlags && pSrcMesh.extraModelInfo)
	{
		if(nlrsScalingFlags[0])
			nlrsScales[0] = scaleX;
		else
			naiveScales[0] = scaleX;
		if(nlrsScalingFlags[1])
			nlrsScales[1] = scaleY;
		else
			naiveScales[1] = scaleY;					
		if(nlrsScalingFlags[2])
			nlrsScales[2] = scaleZ;
		else
			naiveScales[2] = scaleZ;
	}
    else
        naiveScales = [scaleX, scaleY, scaleZ];
	
	if(bInWorldSpace)
	{
		pSrcMesh.computeWorldMatrix(true);

		let cumulMatrix = pSrcMesh.getWorldMatrix().clone();
		cumulMatrix.m[12] = 0;
		cumulMatrix.m[13] = 0;
		cumulMatrix.m[14] = 0;
		let matScaling = BABYLON.Matrix.Scaling(naiveScales[0], naiveScales[1], naiveScales[2]);

		let finalMatrix = cumulMatrix.multiply(matScaling);

		let vecScale = new BABYLON.Vector3();
		let vecTranslations = new BABYLON.Vector3();
		let quatRotation = new BABYLON.Quaternion();
		finalMatrix.decompose(vecScale, quatRotation, vecTranslations);

		let vecAngles = quatRotation.toEulerAngles();
		pSrcMesh.rotation = vecAngles;
		pSrcMesh.scaling = vecScale;
	}
	else
	{
		pSrcMesh.scaling.x *= naiveScales[0];
		pSrcMesh.scaling.y *= naiveScales[1];
		pSrcMesh.scaling.z *= naiveScales[2];
	}

	if(pSrcMesh.extraModelInfo && nlrsScalingFlags && (nlrsScalingFlags[0] || nlrsScalingFlags[1] || nlrsScalingFlags[2]))
	{				
		const nlrs = NonLinearRescaler.setupNonLinearScalingForBabylonMesh(pSrcMesh);					
		// Our native models have their axes flipped:
		let finalScales = nlrsScales;
		if(bInWorldSpace)
			finalScales = [nlrsScales[0], nlrsScales[2], nlrsScales[1]];
		nlrs.rescale('s', finalScales);
		delete pSrcMesh.nlrs;
	}

}
/*****************************************************************************/
static detachSubmeshes(mesh, scene, optPerSubmeshCallback = null)
{
    if(mesh.subMeshes.length <= 1)
        return [];

    let otherVertexData = BABYLON.VertexData.ExtractFromMesh(mesh, true, true);
    let indices = otherVertexData.indices;
    let normals = otherVertexData.normals;
    let positions = otherVertexData.positions;
    let uvs = otherVertexData.uvs;
    let newMeshArray = [];
    for(let index = 0; index < mesh.subMeshes.length; index++)
    {
        let newVertexData = new BABYLON.VertexData();
            
        let newI = indices.slice(mesh.subMeshes[index].indexStart, mesh.subMeshes[index].indexStart+mesh.subMeshes[index].indexCount);
        let newN = normals.slice(mesh.subMeshes[index].verticesStart * 3, mesh.subMeshes[index].verticesStart * 3 + mesh.subMeshes[index].verticesCount * 3);
        let newP = positions.slice(mesh.subMeshes[index].verticesStart * 3, mesh.subMeshes[index].verticesStart * 3 + mesh.subMeshes[index].verticesCount * 3);
        let newU = uvs.slice(mesh.subMeshes[index].verticesStart * 2, mesh.subMeshes[index].verticesStart * 2 + mesh.subMeshes[index].verticesCount * 2);
        for(let subIndex = 0; subIndex < newI.length; subIndex++)
            newI[subIndex] = newI[subIndex] - mesh.subMeshes[index].verticesStart;

        newVertexData.indices = newI;
        newVertexData.normals = newN;
        newVertexData.positions = newP;
        newVertexData.uvs = newU;
            
        let meshSubclass = new BABYLON.Mesh(mesh.name + '-' + index, scene);
        newVertexData.applyToMesh(meshSubclass);

        // Copy over the transforms, otherwise, our positions would be incorrect:
        meshSubclass.position = mesh.position.clone();
        meshSubclass.scaling = mesh.scaling.clone();
        meshSubclass.rotation = mesh.rotation.clone();
        if(mesh.rotationQuaternion)
            meshSubclass.rotationQuaternion = mesh.rotationQuaternion.clone();

		let bDoKeep = true;
        if(optPerSubmeshCallback)
            bDoKeep = optPerSubmeshCallback(meshSubclass, mesh.subMeshes[index].indexStart, mesh.subMeshes[index].indexCount);

		if(bDoKeep)
			newMeshArray.push(meshSubclass);
		else
			meshSubclass.dispose();
    }

    return newMeshArray;
}
/*****************************************************************************/
static computeMeshCrossSectionPath(pSrcMesh, vecPosition, vecUpAxis, vecDirectionOnSurface = null)
{
	if(!pSrcMesh)
		return null;

    // TODO: Known issue - if the line happens to fall on an exact vertex while tracing the contour,
    // it will stop there.

    // Find the start point
    pSrcMesh.computeWorldMatrix();

    let bbox = pSrcMesh.getBoundingInfo().boundingBox;
    let testDirs = [new BABYLON.Vector3(1, 0, 0),
        new BABYLON.Vector3(-1, 0, 0),
        new BABYLON.Vector3(0, 0, 1),
        new BABYLON.Vector3(0, 0, -1),
    ];

    // Get the size along the up axis:
    /*
    let fProjectedMax = BABYLON.Vector3.Dot(vecUpAxis, bbox.maximumWorld);
    let fProjectedMin = BABYLON.Vector3.Dot(vecUpAxis, bbox.minimumWorld);
    let fProjectedSize = (fProjectedMax - fProjectedMin);
    let fProjectedOffset = fProjectedSize*fStartHeightPerc - fProjectedSize/2.0;
    */
    let fProjectedOffset = BABYLON.Vector3.Dot(vecUpAxis, vecPosition.subtract(bbox.centerWorld));

    let rayStart = bbox.centerWorld.add(vecUpAxis.scale(fProjectedOffset));
    let pRay = new BABYLON.Ray(rayStart, null);

    let pickingInfo;
    for(let testDir of testDirs)
	{
        pRay.direction = testDir;
        pickingInfo = pRay.intersectsMesh(pSrcMesh);
        if(pickingInfo.hit && pickingInfo.distance > 0)
            break;
	}

    if(!pickingInfo || !pickingInfo.hit)
	{
        console.error(`Could not find initial mesh intersection point`);
        return null;
	}

    // Start tracing in the given direction
	let vecUpAxisOut = BABYLON.Vector3.Zero();
	let vecSideAxisOut = BABYLON.Vector3.Zero();
	Utils3D.getCoordSystemFromNormal(pickingInfo.getNormal(), vecUpAxisOut, vecSideAxisOut);

    let meshInfo = new MeshInfo(pSrcMesh, 1e-5);
    meshInfo.computeAdjacentFaces();

    let startFaceInfo = meshInfo.getFaceContainingPoint(pickingInfo.pickedPoint);

    if(!vecDirectionOnSurface)
	    vecDirectionOnSurface = new BABYLON.Vector2(1, 0);

    let sideDir = vecSideAxisOut.scale(vecDirectionOnSurface.x).add(vecUpAxisOut.scale(vecDirectionOnSurface.y));
    sideDir.normalize();

    let finalTraversedSet = new Set();

    let selfIntersectPoint;
    let selfAdjacentFaceInfo;

    let vecFinalPoints = [];
    let triIndices = [];

    let currFaceInfo = startFaceInfo;
    let testRay = new BABYLON.Ray(pickingInfo.pickedPoint, sideDir);
    let iter;
    const MAX_ITERS = 50000;
    for(iter = 0; iter < MAX_ITERS; iter++)
	{
		let fSelfIntersectDist = -1;
        let iSelfIntersectedSide = -1;
        for(let iCurrSelfSide = 0; iCurrSelfSide < 3; iCurrSelfSide++)
		{
			let fIntersectDist = testRay.intersectionSegment(currFaceInfo.globalVerts[iCurrSelfSide], currFaceInfo.globalVerts[(iCurrSelfSide + 1) % 3], EPSILON);
			if(fIntersectDist >= 0 && fIntersectDist > fSelfIntersectDist)
			{
				fSelfIntersectDist = fIntersectDist;
                iSelfIntersectedSide = iCurrSelfSide;
			}
		}

        if(iter > 0)
            finalTraversedSet.add(currFaceInfo.index);
		if(fSelfIntersectDist < 0)
            break;

		selfIntersectPoint = testRay.origin.add(testRay.direction.scale(fSelfIntersectDist));
		selfAdjacentFaceInfo = meshInfo.getAdjacentFace(currFaceInfo, iSelfIntersectedSide, finalTraversedSet, selfIntersectPoint, -0.003);

        // Note that this currently includes the duplicate first and last point for closed areas
        vecFinalPoints.push(selfIntersectPoint);
        triIndices.push(currFaceInfo.index);

        if(!selfAdjacentFaceInfo)
            break;

        // Make them current:
        testRay.direction = Utils3D.computeNextDirectionAlongSurface(testRay.direction, currFaceInfo.normal, selfAdjacentFaceInfo.normal, vecUpAxisOut);
        testRay.origin = selfIntersectPoint.clone();
        currFaceInfo = selfAdjacentFaceInfo;
	}
    
    if(iter >= MAX_ITERS)
        console.warn(`Reached max iters circuit breaker!`);

    if(vecFinalPoints.length < 2)
        return null;

    return { points: vecFinalPoints, indices: triIndices };
}
/*****************************************************************************/
static deformPointToRef(vecRelPoint, latticePoints, weightFunctions, vecPointOut)
{
	// Go through all the lattice points and compute weights
	const iNumXSubdivs = latticePoints.length;
	const iNumYSubdivs = latticePoints[0].length;
	const iNumZSubdivs = latticePoints[0][0].length;

	let vecCumulDisplacement = BABYLON.Vector3.Zero();
	for(let iY = 0; iY < iNumYSubdivs; iY++)
	{
		const fYWeight = weightFunctions[iY](vecRelPoint.y);
		for(let iX = 0; iX < iNumXSubdivs; iX++)
		{
			const fXWeight = weightFunctions[iX](vecRelPoint.x);
			for(let iZ = 0; iZ < iNumZSubdivs; iZ++)
			{
				const fZWeight = weightFunctions[iZ](vecRelPoint.z);

				const fTotalWeight = fXWeight*fYWeight*fZWeight;
				vecCumulDisplacement.x += latticePoints[iX][iY][iZ].x*fTotalWeight;
				vecCumulDisplacement.y += latticePoints[iX][iY][iZ].y*fTotalWeight;
				vecCumulDisplacement.z += latticePoints[iX][iY][iZ].z*fTotalWeight;
			}
		}
	}

	vecRelPoint.addToRef(vecCumulDisplacement, vecPointOut);
}
/*****************************************************************************/
static deformSingleMesh(pMesh, latticePoints, weightFunctions, bboxMin, bboxMax)
{
	if(!pMesh)
		return;

	pMesh.makeGeometryUnique();

	let newPositions = [];
	let positions = pMesh.getVerticesData(BABYLON.VertexBuffer.PositionKind);			

	pMesh.computeWorldMatrix(true);
	let worldMatrix = pMesh.getWorldMatrix();

	let invFinalTransformMatrix = BABYLON.Matrix.Identity();
	worldMatrix.invertToRef(invFinalTransformMatrix);

	let vecTemp = BABYLON.Vector3.Zero();
	let vecFinalPoint = BABYLON.Vector3.Zero();
	let vecWorldPos = BABYLON.Vector3.Zero();
	let vecDiff = BABYLON.Vector3.Zero();
	let vecDiff2 = BABYLON.Vector3.Zero();

	let vecSize = bboxMax.subtract(bboxMin);
	// Prevent divison by zero
	if(Math.abs(vecSize.x) < 1e-3)
		vecSize.x = 1.0;
	if(Math.abs(vecSize.y) < 1e-3)
		vecSize.y = 1.0;
	if(Math.abs(vecSize.z) < 1e-3)
		vecSize.z = 1.0;

	for(let index = 0; index < positions.length; index += 3)
	{		
		vecTemp.x = positions[index];
		vecTemp.y = positions[index + 1];
		vecTemp.z = positions[index + 2];

		BABYLON.Vector3.TransformCoordinatesToRef(vecTemp, worldMatrix, vecWorldPos);

		// Convert to a 0-1 range:
		vecWorldPos.subtractToRef(bboxMin, vecDiff);

		vecDiff.x /= vecSize.x;
		vecDiff.y /= vecSize.y;
		vecDiff.z /= vecSize.z;

		GeoUtils.deformPointToRef(vecDiff, latticePoints, weightFunctions, vecDiff2);

		// Convert back to real scale
		vecDiff2.x *= vecSize.x;
		vecDiff2.y *= vecSize.y;
		vecDiff2.z *= vecSize.z;

		vecDiff2.addInPlace(bboxMin);
		BABYLON.Vector3.TransformCoordinatesToRef(vecDiff2, invFinalTransformMatrix, vecFinalPoint);

		newPositions[index] = vecFinalPoint.x;
		newPositions[index + 1] = vecFinalPoint.y;
		newPositions[index + 2] = vecFinalPoint.z;
	}

	pMesh.setVerticesData(BABYLON.VertexBuffer.PositionKind, newPositions, true);
}
/*****************************************************************************/
static taper(pGeoList, strDirection, fStartValue, fEndValue, bValuesAsSizePerc = false, fStartValueNeg = null, fEndValueNeg = null)
{
	let bbox = Utils3D.getObjectsBBoxWithChildren(pGeoList);
	let size = bbox.maximumWorld.subtract(bbox.minimumWorld);

	let fStartValuePos = fStartValue;
	let fEndValuePos = fEndValue;
	if(fStartValueNeg === null)
		fStartValueNeg = fStartValuePos;
	if(fEndValueNeg === null)
		fEndValueNeg = fEndValuePos;

	if(Math.abs(fStartValuePos) < EPSILON && Math.abs(fStartValueNeg) < EPSILON
		&& Math.abs(fEndValueNeg) < EPSILON && Math.abs(fEndValueNeg) < EPSILON)
		return;

	let vecStartScalePos;
	let vecStartScaleNeg;
	let vecEndScalePos;
	let vecEndScaleNeg;
	if(bValuesAsSizePerc)
	{
		vecStartScalePos = new BABYLON.Vector3(fStartValuePos, fStartValuePos, fStartValuePos);
		vecStartScaleNeg = new BABYLON.Vector3(fStartValueNeg, fStartValueNeg, fStartValueNeg);
		vecEndScalePos = new BABYLON.Vector3(fEndValuePos, fEndValuePos, fEndValuePos);
		vecEndScaleNeg = new BABYLON.Vector3(fEndValueNeg, fEndValueNeg, fEndValueNeg);
	}
	else
	{
		vecStartScalePos = new BABYLON.Vector3(fStartValuePos/size.x, fStartValuePos/size.y, fStartValuePos/size.z);
		vecStartScaleNeg = new BABYLON.Vector3(fStartValueNeg/size.x, fStartValueNeg/size.y, fStartValueNeg/size.z);
		vecEndScalePos = new BABYLON.Vector3(fEndValuePos/size.x, fEndValuePos/size.y, fEndValuePos/size.z);
		vecEndScaleNeg = new BABYLON.Vector3(fEndValueNeg/size.x, fEndValueNeg/size.y, fEndValueNeg/size.z);
	}

	let latticePoints = [[[], []], [[], []]];
	for(let iY = 0; iY < 2; iY++)
	{
		for(let iX = 0; iX < 2; iX++)
		{
			for(let iZ = 0; iZ < 2; iZ++)
				latticePoints[iX][iY][iZ] = BABYLON.Vector3.Zero();
		}
	}

	if(strDirection === 'xAxis' || strDirection === 'xyPlane')
	{
		latticePoints[0][0][0].x += vecStartScalePos.x;
		latticePoints[0][1][0].x += vecStartScalePos.x;
		latticePoints[0][0][1].x += vecEndScalePos.x;
		latticePoints[0][1][1].x += vecEndScalePos.x;

		latticePoints[1][0][0].x -= vecStartScaleNeg.x;
		latticePoints[1][1][0].x -= vecStartScaleNeg.x;
		latticePoints[1][0][1].x -= vecEndScaleNeg.x;
		latticePoints[1][1][1].x -= vecEndScaleNeg.x;
	}
	else if(strDirection === 'xzPlane')
	{
		latticePoints[0][1][0].x += vecStartScalePos.x;
		latticePoints[0][1][1].x += vecStartScalePos.x;
		latticePoints[0][0][0].x += vecEndScalePos.x;
		latticePoints[0][0][1].x += vecEndScalePos.x;

		latticePoints[1][1][0].x -= vecStartScaleNeg.x;
		latticePoints[1][1][1].x -= vecStartScaleNeg.x;
		latticePoints[1][0][0].x -= vecEndScaleNeg.x;
		latticePoints[1][0][1].x -= vecEndScaleNeg.x;
	}

	if(strDirection === 'yAxis' || strDirection === 'xyPlane')
	{
		latticePoints[0][0][0].y += vecStartScalePos.y;
		latticePoints[1][0][0].y += vecStartScalePos.y;
		latticePoints[0][1][0].y -= vecStartScaleNeg.y;
		latticePoints[1][1][0].y -= vecStartScaleNeg.y;

		latticePoints[0][0][1].y += vecEndScalePos.y;
		latticePoints[1][0][1].y += vecEndScalePos.y;
		latticePoints[0][1][1].y -= vecEndScaleNeg.y;
		latticePoints[1][1][1].y -= vecEndScaleNeg.y;
	}
	else if(strDirection === 'yzPlane')
	{
		latticePoints[1][0][0].y += vecStartScalePos.y;
		latticePoints[1][0][1].y += vecStartScalePos.y;
		latticePoints[1][1][0].y -= vecStartScaleNeg.y;
		latticePoints[1][1][1].y -= vecStartScaleNeg.y;

		latticePoints[0][0][0].y += vecEndScalePos.y;
		latticePoints[0][0][1].y += vecEndScalePos.y;
		latticePoints[0][1][0].y -= vecEndScaleNeg.y;
		latticePoints[0][1][1].y -= vecEndScaleNeg.y;
	}

	if(strDirection === 'zAxis' || strDirection === 'xzPlane')
	{
		latticePoints[0][1][1].z -= vecStartScaleNeg.z;
		latticePoints[1][1][1].z -= vecStartScaleNeg.z;
		latticePoints[0][1][0].z += vecStartScalePos.z;
		latticePoints[1][1][0].z += vecStartScalePos.z;

		latticePoints[0][0][1].z -= vecEndScaleNeg.z;
		latticePoints[1][0][1].z -= vecEndScaleNeg.z;
		latticePoints[0][0][0].z += vecEndScalePos.z;
		latticePoints[1][0][0].z += vecEndScalePos.z;
	}
	else if(strDirection === 'yzPlane')
	{
		latticePoints[1][0][1].z -= vecStartScaleNeg.z;
		latticePoints[1][0][0].z += vecStartScalePos.z;
		latticePoints[1][1][1].z -= vecStartScaleNeg.z;
		latticePoints[1][1][0].z += vecStartScalePos.z;

		latticePoints[0][0][1].z -= vecEndScaleNeg.z;
		latticePoints[0][0][0].z += vecEndScalePos.z;
		latticePoints[0][1][1].z -= vecEndScaleNeg.z;
		latticePoints[0][1][0].z += vecEndScalePos.z;
	}

	const TAPER_WEIGHT_FUNCTIONS = [
		(x) => { return x; },
		(x) => { return 1 - x; },
	];

	for(let pSrcMesh of pGeoList)
		GeoUtils.deformSingleMesh(pSrcMesh, latticePoints, TAPER_WEIGHT_FUNCTIONS, bbox.minimumWorld, bbox.maximumWorld);
}
/*****************************************************************************/
static removeTrianglesByFaceIndices(pSrcMesh, faceIndicesToRemove)
{
	if(faceIndicesToRemove.length == 0)
		return;

    const indices = pSrcMesh.getIndices();
    const faceIndicesSet = new Set(faceIndicesToRemove);
    const newIndices = [];
    const newSubMeshes = [];

    // Process each submesh
    for(const subMesh of pSrcMesh.subMeshes)
	{
        const indexStart = subMesh.indexStart;
        const indexCount = subMesh.indexCount;
        const newSubMeshIndices = [];

        // Process triangles in current submesh
        for(let i = indexStart; i < indexStart + indexCount; i += 3)
		{
            // Skip triangle if its starting index is in faceIndicesToRemove
            if(!faceIndicesSet.has(i))
                newSubMeshIndices.push(indices[i], indices[i + 1], indices[i + 2]);
        }

        // Store new submesh data if it has valid triangles
        if(newSubMeshIndices.length > 0)
		{
            newSubMeshes.push({
                materialIndex: subMesh.materialIndex,
                indexStart: newIndices.length,
                indexCount: newSubMeshIndices.length,
                vertexStart: subMesh.vertexStart,
                vertexCount: subMesh.vertexCount
            });
            newIndices.push(...newSubMeshIndices);
        }
    }

    // Update mesh with new indices and submeshes
	pSrcMesh.subMeshes = [];
	pSrcMesh.updateIndices(newIndices);
    if(newIndices.length > 0)
	{
        newSubMeshes.forEach(sm => { new BABYLON.SubMesh(sm.materialIndex, sm.vertexStart, sm.vertexCount, sm.indexStart, sm.indexCount, pSrcMesh); });
    }
	else
	{
        // Handle case where all triangles are removed
        new BABYLON.SubMesh(0, 0, 0, 0, 0, pSrcMesh);
    }
}
/*****************************************************************************/
}